import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Panel, DataTable, fmtDate } from "./shared";
import { AogTag } from "@/components/shipment/AogTag";

function AckCell({ r, onDone }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  if (r.status === "Acknowledged") {
    const a = r.acknowledgement || {};
    return <div className="text-xs text-emerald-300">By {a.by_name}<div className="text-slate-500">{new Date(a.at).toLocaleString()}</div></div>;
  }
  if (!r.mine) return <span className="text-xs text-amber-300">Waiting for {r.assigned_biller?.name}</span>;
  const ack = async () => {
    setBusy(true);
    try { await api.post(`/shipments/${r.shipment_id}/transmittal/acknowledge`, { remarks }); toast.success(`${r.shipment_number} acknowledged`); onDone(); }
    catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 min-w-[260px]">
      <Input data-testid={`ack-remarks-${r.shipment_id}`} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (optional)"
             className="h-8 bg-slate-950 border-slate-700 text-xs" />
      <Button data-testid={`ack-transmittal-${r.shipment_id}`} size="sm" onClick={ack} disabled={busy} className="h-8 bg-emerald-600 hover:bg-emerald-500 shrink-0">
        {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <CheckCircle2 size={12} className="mr-1" />} Receive & acknowledge
      </Button>
    </div>
  );
}

export default function TransmittalsTab({ onChanged }) {
  const [status, setStatus] = useState("Transmitted");
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/finance/transmittals?status=${status}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const cols = [
    { key: "shipment_number", label: "Shipment #", render: (r) => (
      <div className="flex items-center gap-2"><Link to={`/shipments/${r.shipment_id}`} className="font-mono-ops text-sky-300 hover:text-sky-200">{r.shipment_number}</Link><AogTag s={{ pre_alert: { priority: r.aog ? "AOG" : "" } }} /></div>) },
    { key: "transmittal_number", label: "Transmittal #", mono: true },
    { key: "client_name", label: "Client" },
    { key: "mawb_number", label: "MAWB", mono: true },
    { key: "turnover_date", label: "Turnover", render: (r) => fmtDate(r.turnover_date) },
    { key: "delivered_by", label: "Delivered by", render: (r) => r.delivered_by?.name || "—" },
    { key: "assigned_biller", label: "Assigned biller", render: (r) => r.assigned_biller?.name || "—" },
    { key: "ack", label: status === "Acknowledged" ? "Acknowledged" : "Acknowledgement", render: (r) => <AckCell r={r} onDone={() => { load(); onChanged?.(); }} /> },
  ];

  return (
    <Panel title="Billing Transmittals" subtitle="Transmittals turned over to billing. The assigned biller receives and acknowledges each one here; the shipment then moves to Completed and can be invoiced."
           right={<Tabs value={status} onValueChange={setStatus}><TabsList>
             <TabsTrigger value="Transmitted" data-testid="transmittals-filter-pending">To acknowledge</TabsTrigger>
             <TabsTrigger value="Acknowledged" data-testid="transmittals-filter-done">Acknowledged</TabsTrigger>
           </TabsList></Tabs>}>
      <DataTable testid="transmittals-table" columns={cols} rows={rows}
                 empty={status === "Transmitted" ? "No transmittals waiting for acknowledgement." : "No acknowledged transmittals yet."} />
    </Panel>
  );
}
